/**
 * /development/collections/new — the create wizard.
 *
 * 1. **Details** — name (required, unique: checked against the caller's list in
 *    the form; a `409` from the API is still handled, since the list is only
 *    what this caller can see) and description. "Create" makes the record.
 * 2. **Documents** — add from Azure Blob, Upload or Veeva Vault.
 * 3. **Build** — start the build and land on the detail page, which shows
 *    live progress.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Paper,
  Stack,
  Stepper,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { IconArrowRight, IconHammer } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import {
  buildIndex,
  createIndex,
  listIndexes,
  type IndexOut,
  type IndexedDocumentOut,
} from "@/shared/api/indexes";
import { toastApiError, toastWarning } from "@/shared/ui/toast";
import { AddDocumentsPanel } from "./AddDocumentsPanel";
import { SourceIcon } from "./SourceIcon";

export function NewCollection() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [existing, setExisting] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [serverNameError, setServerNameError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<IndexOut | null>(null);
  const [added, setAdded] = useState<IndexedDocumentOut[]>([]);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const token = await getToken();
        const page = await listIndexes(token);
        setExisting(page.items.map((i) => i.name.trim().toLowerCase()));
      } catch {
        // The API still refuses a duplicate; the form check is a convenience.
      }
    })();
  }, [getToken]);

  const trimmed = name.trim();
  const nameError = useMemo(() => {
    if (serverNameError) return serverNameError;
    if (trimmed && existing.includes(trimmed.toLowerCase())) {
      return "A collection with this name already exists.";
    }
    return null;
  }, [trimmed, existing, serverNameError]);

  const create = async () => {
    if (!trimmed || nameError) return;
    setCreating(true);
    try {
      const token = await getToken();
      const row = await createIndex(token, {
        name: trimmed,
        description: description.trim() || null,
      });
      setCreated(row);
      setStep(1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerNameError("A collection with this name already exists.");
      } else {
        toastApiError("Could not create the collection", err);
      }
    } finally {
      setCreating(false);
    }
  };

  const build = async () => {
    if (!created) return;
    setBuilding(true);
    try {
      const token = await getToken();
      await buildIndex(token, created.index_id);
    } catch (err) {
      if (err instanceof ApiError && err.code === "document_index_build_unavailable") {
        toastWarning(
          "Building is not available here",
          "No embedding model is configured in this environment. Your documents are saved; build later.",
        );
      } else {
        toastApiError("Could not start the build", err);
      }
    } finally {
      setBuilding(false);
    }
    navigate(`/development/collections/${created.index_id}`);
  };

  const detailPath = created ? `/development/collections/${created.index_id}` : "";

  return (
    <Stack gap="md" maw={960}>
      <Title order={2}>New collection</Title>
      <Stepper active={step} allowNextStepsSelect={false} size="sm">
        <Stepper.Step label="Details" description="Name and description">
          <Paper withBorder radius="md" p="md" mt="md">
            <Stack gap="sm">
              <TextInput
                label="Name"
                required
                data-autofocus
                value={name}
                error={nameError}
                onChange={(e) => {
                  setName(e.currentTarget.value);
                  setServerNameError(null);
                }}
              />
              <Textarea
                label="Description"
                autosize
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button
                  onClick={() => void create()}
                  loading={creating}
                  disabled={!trimmed || nameError !== null}
                  rightSection={<IconArrowRight size={16} />}
                >
                  Create and add documents
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Documents" description="Blob, upload or Veeva">
          {created && (
            <Stack gap="md" mt="md">
              <Paper withBorder radius="md" p="md">
                <AddDocumentsPanel
                  indexId={created.index_id}
                  onAdded={(r) => setAdded((prev) => [...prev, ...r.added])}
                />
              </Paper>
              <AddedList docs={added} />
              <Group justify="space-between">
                <Button variant="subtle" onClick={() => navigate(detailPath)}>
                  Finish later
                </Button>
                <Button
                  onClick={() => setStep(2)}
                  disabled={added.length === 0}
                  rightSection={<IconArrowRight size={16} />}
                >
                  Next: build
                </Button>
              </Group>
            </Stack>
          )}
        </Stepper.Step>

        <Stepper.Step label="Build" description="Index the documents">
          <Paper withBorder radius="md" p="md" mt="md">
            <Stack gap="sm">
              <Text>
                Build <b>{created?.name}</b> from {added.length} document
                {added.length === 1 ? "" : "s"}. Each document is read, split into passages and
                indexed; you can follow progress on the next page.
              </Text>
              <Alert variant="light" color="gray">
                Source files are only read. Nothing is written back to Blob or Veeva.
              </Alert>
              <Group justify="space-between">
                <Button variant="default" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button
                  leftSection={<IconHammer size={16} />}
                  onClick={() => void build()}
                  loading={building}
                >
                  Build
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stepper.Step>
      </Stepper>
    </Stack>
  );
}

function AddedList({ docs }: { docs: IndexedDocumentOut[] }) {
  if (docs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No documents added yet.
      </Text>
    );
  }
  return (
    <Paper withBorder radius="md" p="xs">
      <Text size="sm" fw={500} mb={4}>
        Added so far ({docs.length})
      </Text>
      <Table verticalSpacing={4}>
        <Table.Tbody>
          {docs.map((d) => (
            <Table.Tr key={d.document_id}>
              <Table.Td style={{ width: 36 }}>
                <SourceIcon kind={d.source_kind} />
              </Table.Td>
              <Table.Td>
                <Text size="sm">{d.display_name}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" c={d.status === "skipped" ? "yellow.8" : "dimmed"}>
                  {d.status === "skipped" ? `will be skipped: ${d.status_reason ?? ""}` : "pending"}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
